/**
 * Meetings — Phase 2b implementation.
 *
 * Sections (§4 spec):
 *   1. "Your meetings": upcoming + past cards with status stamps (requested/
 *      proposed=rotated amber stamp, confirmed=flattened amber stamp,
 *      declined/cancelled=flattened muted stamp) + local time + client_note.
 *      Cancel button on requested/confirmed future meetings.
 *      Reschedule = cancel then re-book.
 *   2. "Request a meeting": constrained slot picker.
 *      - Open meeting gate → "You have a meeting booked — cancel it to request another."
 *      - No slots → "No open times this period."
 *      - Otherwise → horizontal day tabs → time chips → optional note → Confirm request.
 *      409 responses shown as friendly inline messages.
 *
 * Data (queries):
 *   GET  /api/console/meetings           → key ['meetings']
 *   GET  /api/console/booking/slots      → key ['slots']
 *   POST /api/console/booking/request    → useMutation
 *   POST /api/console/booking/:id/cancel → useMutation
 *   POST /api/console/booking/:id/accept → useMutation
 *
 * Visual system: "Site Office" (see src/styles/global.css) — meeting cards
 * use the .ticket shell (perforated notch + dashed stub divider), day/slot
 * pickers use .datetab / .chipx, notes use .tax.
 */
import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../lib/api.js';
import Skeleton from '../components/Skeleton.jsx';
import {
  CalendarDays,
  Clock,
  Check,
  X,
  AlertCircle,
  MessageSquare,
  Video,
} from 'lucide-react';

// ── Helpers ───────────────────────────────────────────────────────────────────

// Clients are Texas businesses, so meeting + slot times always render in Central
// Time (labelled "CT"), regardless of the viewer's device timezone. Day bucketing
// for the date strip also happens in CT so a slot lands on its Texas calendar day.
const CLIENT_TZ = 'America/Chicago';

/** YYYY-MM-DD key derived from a UTC ISO string, in Central Time. */
function getLocalDateKey(utcIso) {
  // en-CA → "YYYY-MM-DD"; timeZone forces the Texas calendar day.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: CLIENT_TZ, year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date(utcIso));
}

/** Display label for the date strip (e.g. "Mon, Jul 7"). */
function formatDateLabel(key) {
  const [y, mo, da] = key.split('-').map(Number);
  const d = new Date(y, mo - 1, da);
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

/** "9:00 AM CT" from a UTC ISO string, in Central Time. */
function formatSlotTime(utcIso) {
  return new Date(utcIso).toLocaleTimeString(undefined, {
    hour: 'numeric', minute: '2-digit', timeZone: CLIENT_TZ,
  }) + ' CT';
}

/** Full datetime for a meeting card (e.g. "Mon, Jul 7, 9:00 AM CT"). */
function formatMeetingTime(utcIso) {
  return new Date(utcIso).toLocaleString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit', timeZone: CLIENT_TZ,
  }) + ' CT';
}

/** Ticket-stub day number + month abbreviation, in Central Time. */
function formatStub(utcIso) {
  const d = new Date(utcIso);
  return {
    day: new Intl.DateTimeFormat('en-US', { day: '2-digit', timeZone: CLIENT_TZ }).format(d),
    month: new Intl.DateTimeFormat('en-US', { month: 'short', timeZone: CLIENT_TZ }).format(d).toUpperCase(),
  };
}

// ── Status stamp ────────────────────────────────────────────────────────────────

// flat = filed/resolved (flattened stamp); non-flat = provisional (crooked, freshly stamped)
const STAMP_STYLES = {
  confirmed: { color: 'var(--acc-ink)', flat: true  },
  requested: { color: 'var(--acc-ink)', flat: false },
  proposed:  { color: 'var(--acc-ink)', flat: false },
  declined:  { color: 'var(--ink-soft)', flat: true },
  cancelled: { color: 'var(--ink-soft)', flat: true },
};

function StatusStamp({ status }) {
  const s = STAMP_STYLES[status] || STAMP_STYLES.requested;
  return (
    <span className={`stamp stamp-in${s.flat ? ' flat' : ''}`} style={{ color: s.color }}>
      {status}
    </span>
  );
}

// ── Meeting card (ticket) ───────────────────────────────────────────────────────

function MeetingCard({ meeting, onCancel, cancelPending, cancelError, onAccept, acceptPending, durationMinutes = 30 }) {
  const nowMs = Date.now();
  const isFuture = new Date(meeting.start_at).getTime() > nowMs;
  const isProposed = meeting.status === 'proposed';
  const canCancel = isFuture && (meeting.status === 'requested' || meeting.status === 'confirmed');
  const stub = formatStub(meeting.start_at);

  return (
    <div className="ticket rv" style={{ display: 'flex' }}>
      <span className="notch top" aria-hidden="true" />
      <span className="notch bot" aria-hidden="true" />

      {/* Stub — date */}
      <div
        style={{
          width: '64px',
          flexShrink: 0,
          padding: '14px 0',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <span className="d-xl" style={{ fontSize: '22px' }}>{stub.day}</span>
        <span className="mlabel" style={{ marginTop: '2px' }}>{stub.month}</span>
      </div>

      {/* Main content — right of the dashed divider */}
      <div style={{ flex: 1, minWidth: 0, padding: '14px 16px 14px 20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {/* Top row: time + stamp */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px' }}>
          <div style={{ minWidth: 0 }}>
            <p className="mono" style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: 'var(--ink)', lineHeight: 1.3 }}>
              {formatMeetingTime(meeting.start_at)}
            </p>
            <p className="mlabel" style={{ marginTop: '3px' }}>
              {meeting.kind === 'kickoff' ? 'Kickoff call' : `${durationMinutes}-min check-in`}
            </p>
          </div>
          <StatusStamp status={meeting.status} />
        </div>

        {/* Join link — only once the operator's Google Calendar generated a Meet link */}
        {meeting.status === 'confirmed' && meeting.meet_link && (
          <a
            href={meeting.meet_link}
            target="_blank"
            rel="noreferrer"
            className="btnx"
            style={{ borderColor: 'var(--ok)', color: 'var(--ok)', height: '44px' }}
          >
            <Video size={16} strokeWidth={2.4} aria-hidden="true" />
            Join Google Meet
          </a>
        )}

        {/* Client note */}
        {meeting.client_note && (
          <div className="lrow" style={{ alignItems: 'flex-start', gap: '8px', background: 'var(--paper)' }}>
            <MessageSquare size={13} color="var(--ink-soft)" strokeWidth={1.75} style={{ flexShrink: 0, marginTop: '2px' }} aria-hidden="true" />
            <p style={{ margin: 0, fontSize: '13px', fontFamily: 'var(--font-body)', color: 'var(--ink-soft)', lineHeight: 1.45 }}>
              {meeting.client_note}
            </p>
          </div>
        )}

        {/* Operator-proposed alternate time — accept or decline */}
        {isProposed && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div className="lrow" style={{ alignItems: 'flex-start', gap: '8px', borderColor: 'var(--acc)' }}>
              <CalendarDays size={13} color="var(--acc)" strokeWidth={2} style={{ flexShrink: 0, marginTop: '2px' }} aria-hidden="true" />
              <p style={{ margin: 0, fontSize: '13px', fontFamily: 'var(--font-body)', color: 'var(--ink)', lineHeight: 1.45 }}>
                We proposed this new time.{meeting.operator_note ? ` ${meeting.operator_note}` : ''} Accept it, or decline to pick another.
              </p>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="button"
                className="btnx primary"
                style={{ flex: 1, height: '42px' }}
                onClick={() => onAccept(meeting.id)}
                disabled={acceptPending || cancelPending}
              >
                <Check size={15} strokeWidth={2.5} aria-hidden="true" />
                {acceptPending ? 'Accepting…' : 'Accept this time'}
              </button>
              <button
                type="button"
                className="btnx quiet"
                style={{ height: '42px' }}
                onClick={() => onCancel(meeting.id)}
                disabled={acceptPending || cancelPending}
              >
                {cancelPending ? 'Declining…' : 'Decline'}
              </button>
            </div>
            {cancelError && (
              <p style={{ margin: 0, fontSize: '12px', color: 'var(--bad)', fontFamily: 'var(--font-body)' }}>{cancelError}</p>
            )}
          </div>
        )}

        {/* Cancel button + error */}
        {canCancel && (
          <div>
            <button
              type="button"
              className="btnx danger"
              onClick={() => onCancel(meeting.id)}
              disabled={cancelPending}
              aria-label={`Cancel meeting scheduled for ${formatMeetingTime(meeting.start_at)}`}
            >
              <X size={13} strokeWidth={2.5} aria-hidden="true" />
              {cancelPending ? 'Cancelling…' : 'Cancel meeting'}
            </button>
            {cancelError && (
              <p style={{ margin: '6px 0 0', fontSize: '12px', color: 'var(--bad)', fontFamily: 'var(--font-body)' }}>{cancelError}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Date strip ────────────────────────────────────────────────────────────────

function DateStrip({ days, selectedDate, onSelect }) {
  return (
    <div
      role="list"
      aria-label="Available days"
      style={{ display: 'flex', overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
    >
      <style>{`div[aria-label="Available days"]::-webkit-scrollbar { display: none; }`}</style>
      {days.map(({ key, label }) => (
        <button
          key={key}
          type="button"
          className="datetab"
          data-on={key === selectedDate}
          aria-pressed={key === selectedDate}
          onClick={() => onSelect(key)}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

// ── Slot chip grid ─────────────────────────────────────────────────────────────

function SlotGrid({ slots, selectedSlot, onSelect }) {
  return (
    <div role="list" aria-label="Available time slots" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
      {slots.map((slot) => {
        const isSelected = selectedSlot?.start_utc === slot.start_utc;
        return (
          <button
            key={slot.start_utc}
            type="button"
            className="chipx"
            data-on={isSelected}
            aria-pressed={isSelected}
            aria-label={`Select ${formatSlotTime(slot.start_utc)}`}
            onClick={() => onSelect(isSelected ? null : slot)}
          >
            {formatSlotTime(slot.start_utc)}
          </button>
        );
      })}
    </div>
  );
}

// ── Skeleton ──────────────────────────────────────────────────────────────────

function MeetingsSkeleton() {
  return (
    <div style={{ padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div className="panel" style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <Skeleton style={{ height: 11, width: '28%' }} />
        <Skeleton style={{ height: 82 }} />
        <Skeleton style={{ height: 70 }} />
      </div>
      <div className="panel" style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <Skeleton style={{ height: 11, width: '38%' }} />
        <div style={{ display: 'flex', gap: '8px' }}>
          {[80, 90, 80].map((w, i) => (
            <Skeleton key={i} style={{ height: 40, width: w, flexShrink: 0 }} />
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} style={{ height: 38 }} />
          ))}
        </div>
        <Skeleton style={{ height: 80 }} />
        <Skeleton style={{ height: 48 }} />
      </div>
    </div>
  );
}

// ── Error ─────────────────────────────────────────────────────────────────────

function MeetingsError({ error, refetch }) {
  return (
    <div
      style={{
        padding: '20px 16px',
        paddingTop: '64px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        gap: '16px',
      }}
    >
      <div
        style={{
          width: '52px',
          height: '52px',
          borderRadius: '50%',
          background: 'var(--panel-2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <AlertCircle size={24} color="var(--ink-soft)" strokeWidth={1.5} aria-hidden="true" />
      </div>
      <div>
        <p className="d-xl" style={{ fontSize: '20px', margin: '0 0 6px' }}>
          Couldn't load meetings
        </p>
        <p style={{ margin: 0, color: 'var(--ink-soft)', fontSize: '14px', lineHeight: 1.5, fontFamily: 'var(--font-body)' }}>
          {error?.message || 'Something went wrong. Tap below to try again.'}
        </p>
      </div>
      <button type="button" className="btnx primary" onClick={() => refetch()}>
        Try again
      </button>
    </div>
  );
}

// ── Section label (reused pattern from Overview/Deliverables) ─────────────────

function SectionLabel({ children }) {
  return (
    <h2 className="mlabel" style={{ margin: '0 0 16px' }}>
      {children}
    </h2>
  );
}

// ── Main screen ───────────────────────────────────────────────────────────────

export default function Meetings() {
  const queryClient = useQueryClient();

  // ── State: picker selections + request error ──────────────────────────────
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [note, setNote] = useState('');
  const [requestError, setRequestError] = useState(null);
  const [cancelErrors, setCancelErrors] = useState({});  // keyed by meeting id

  // ── Queries ────────────────────────────────────────────────────────────────
  const {
    data: meetingsData,
    isLoading: meetingsLoading,
    error: meetingsError,
    refetch: refetchMeetings,
  } = useQuery({
    queryKey: ['meetings'],
    queryFn: () => apiFetch('/api/console/meetings'),
  });

  const meetings = meetingsData?.meetings ?? [];
  const nowMs = Date.now();

  // Open meeting = requested, confirmed, or operator-proposed AND in the future
  const openMeeting = meetings.find(
    (m) =>
      (m.status === 'requested' || m.status === 'confirmed' || m.status === 'proposed') &&
      new Date(m.start_at).getTime() > nowMs,
  );
  const hasOpenMeeting = !!openMeeting;

  const { data: slotsData, isLoading: slotsLoading } = useQuery({
    queryKey: ['slots'],
    queryFn: () => apiFetch('/api/console/booking/slots'),
  });

  // ── Slot grouping ──────────────────────────────────────────────────────────
  const slotDays = useMemo(() => {
    if (!slotsData?.slots?.length) return [];
    const map = new Map();
    for (const slot of slotsData.slots) {
      const key = getLocalDateKey(slot.start_utc);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(slot);
    }
    return [...map.entries()].map(([key, slots]) => ({
      key,
      label: formatDateLabel(key),
      slots,
    }));
  }, [slotsData]);

  // Auto-use first available day if nothing selected yet
  const effectiveDate = selectedDate || (slotDays.length > 0 ? slotDays[0].key : null);
  const activeSlots = slotDays.find((d) => d.key === effectiveDate)?.slots ?? [];

  // ── Sort meetings: upcoming ascending, past descending ─────────────────────
  const upcomingMeetings = meetings
    .filter((m) => new Date(m.start_at).getTime() > nowMs)
    .sort((a, b) => new Date(a.start_at) - new Date(b.start_at));

  const pastMeetings = meetings
    .filter((m) => new Date(m.start_at).getTime() <= nowMs)
    .sort((a, b) => new Date(b.start_at) - new Date(a.start_at));

  const sortedMeetings = [...upcomingMeetings, ...pastMeetings];

  // ── Cancel mutation ────────────────────────────────────────────────────────
  const cancelMutation = useMutation({
    mutationFn: (meetingId) =>
      apiFetch(`/api/console/booking/${meetingId}/cancel`, { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meetings'] });
      queryClient.invalidateQueries({ queryKey: ['overview'] });
      // Invalidate slots so freed availability shows immediately.
      queryClient.invalidateQueries({ queryKey: ['slots'] });
    },
    onError: (err, meetingId) => {
      setCancelErrors((prev) => ({
        ...prev,
        [meetingId]: err?.message || 'Could not cancel. Please try again.',
      }));
    },
  });

  // ── Accept mutation (client accepts an operator-proposed time) ─────────────
  const acceptMutation = useMutation({
    mutationFn: (meetingId) =>
      apiFetch(`/api/console/booking/${meetingId}/accept`, { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meetings'] });
      queryClient.invalidateQueries({ queryKey: ['overview'] });
      queryClient.invalidateQueries({ queryKey: ['slots'] });
    },
    onError: (err, meetingId) => {
      setCancelErrors((prev) => ({
        ...prev,
        [meetingId]: err?.message || 'Could not accept. Please try again.',
      }));
    },
  });

  // ── Request mutation ───────────────────────────────────────────────────────
  const requestMutation = useMutation({
    mutationFn: ({ start_utc, note: n }) =>
      apiFetch('/api/console/booking/request', {
        method: 'POST',
        body: { start_utc, ...(n ? { note: n } : {}) },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meetings'] });
      queryClient.invalidateQueries({ queryKey: ['slots'] });
      queryClient.invalidateQueries({ queryKey: ['overview'] });
      // Reset picker state
      setSelectedSlot(null);
      setSelectedDate(null);
      setNote('');
      setRequestError(null);
    },
    onError: (err) => {
      const code = err?.data?.error;
      if (code === 'open_meeting_exists') {
        setRequestError(
          'A meeting is already booked — cancel it to request another.',
        );
      } else if (code === 'slot_unavailable') {
        setRequestError(
          'That time slot is no longer available. Please pick another.',
        );
      } else {
        setRequestError(err?.message || 'Could not book the meeting. Please try again.');
      }
    },
  });

  // ── Early returns ──────────────────────────────────────────────────────────
  if (meetingsLoading) return <MeetingsSkeleton />;
  if (meetingsError) return <MeetingsError error={meetingsError} refetch={refetchMeetings} />;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* ── Page header ──────────────────────────────────────────────────── */}
      <div>
        <h1 className="d-xl" style={{ fontSize: '30px', margin: '0 0 4px' }}>
          Meetings
        </h1>
        <p style={{ margin: 0, color: 'var(--ink-soft)', fontSize: '15px', lineHeight: 1.55, fontFamily: 'var(--font-body)' }}>
          Schedule and manage your check-ins.
        </p>
      </div>

      {/* ── 1. Your meetings ─────────────────────────────────────────────── */}
      <section aria-label="Your meetings" className="panel rv" style={{ position: 'relative', padding: '16px' }}>
        <span className="brk brk-tl" aria-hidden="true" />
        <span className="brk brk-br" aria-hidden="true" />
        <SectionLabel>Your meetings</SectionLabel>

        {sortedMeetings.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '24px 0 8px', gap: '8px', textAlign: 'center' }}>
            <Clock size={28} color="var(--ink-soft)" strokeWidth={1.5} aria-hidden="true" />
            <p style={{ margin: 0, fontSize: '14px', color: 'var(--ink-soft)', fontFamily: 'var(--font-body)', lineHeight: 1.5 }}>
              No meetings yet — request one below.
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            {sortedMeetings.map((m) => (
              <MeetingCard
                key={m.id}
                meeting={m}
                onCancel={(id) => {
                  setCancelErrors((prev) => ({ ...prev, [id]: null }));
                  cancelMutation.mutate(id);
                }}
                cancelPending={
                  cancelMutation.isPending && cancelMutation.variables === m.id
                }
                cancelError={cancelErrors[m.id] ?? null}
                onAccept={(id) => {
                  setCancelErrors((prev) => ({ ...prev, [id]: null }));
                  acceptMutation.mutate(id);
                }}
                acceptPending={
                  acceptMutation.isPending && acceptMutation.variables === m.id
                }
                durationMinutes={slotsData?.duration_minutes ?? 30}
              />
            ))}
          </div>
        )}
      </section>

      {/* ── 2. Request a meeting ──────────────────────────────────────────── */}
      <section aria-label="Request a meeting" className="panel rv" style={{ position: 'relative', padding: '16px' }}>
        <span className="brk brk-tl" aria-hidden="true" />
        <span className="brk brk-br" aria-hidden="true" />
        <SectionLabel>Request a meeting</SectionLabel>

        {/* Gate: open meeting exists */}
        {hasOpenMeeting ? (
          <div role="status" aria-live="polite" className="lrow" style={{ alignItems: 'flex-start', gap: '12px', borderColor: 'var(--acc)' }}>
            <CalendarDays size={18} color="var(--acc)" strokeWidth={2} style={{ flexShrink: 0, marginTop: '2px' }} aria-hidden="true" />
            <p style={{ margin: 0, fontSize: '14px', fontFamily: 'var(--font-body)', color: 'var(--ink)', lineHeight: 1.5 }}>
              You have a meeting booked — cancel it to request another.
            </p>
          </div>
        ) : slotsLoading ? (
          /* Slots still loading */
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', gap: '8px' }}>
              {[80, 90, 80].map((w, i) => (
                <Skeleton key={i} style={{ height: 40, width: w, flexShrink: 0 }} />
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} style={{ height: 38 }} />
              ))}
            </div>
          </div>
        ) : slotDays.length === 0 ? (
          /* No slots */
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 0 6px' }}>
            <AlertCircle size={18} color="var(--ink-soft)" strokeWidth={1.75} aria-hidden="true" />
            <p style={{ margin: 0, fontSize: '14px', color: 'var(--ink-soft)', fontFamily: 'var(--font-body)' }}>
              No open times this period.
            </p>
          </div>
        ) : (
          /* ── Picker ──────────────────────────────────────────────────── */
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {/* Day tabs */}
            <DateStrip
              days={slotDays}
              selectedDate={effectiveDate}
              onSelect={(key) => {
                setSelectedDate(key);
                setSelectedSlot(null);
              }}
            />

            {/* Slot chips */}
            {activeSlots.length > 0 ? (
              <SlotGrid
                slots={activeSlots}
                selectedSlot={selectedSlot}
                onSelect={(slot) => {
                  setSelectedSlot(slot);
                  setRequestError(null);
                }}
              />
            ) : (
              <p style={{ margin: 0, fontSize: '13px', color: 'var(--ink-soft)', fontFamily: 'var(--font-body)' }}>
                No slots on this day.
              </p>
            )}

            {/* Optional note */}
            {selectedSlot && (
              <div>
                <label htmlFor="meeting-note" className="mlabel" style={{ display: 'block', marginBottom: '6px' }}>
                  Note (optional)
                </label>
                <textarea
                  id="meeting-note"
                  className="tax"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Anything specific you'd like to discuss?"
                  rows={3}
                />
              </div>
            )}

            {/* 409 / mutation error */}
            {requestError && (
              <div role="alert" className="lrow" style={{ gap: '8px', borderColor: 'var(--bad)' }}>
                <AlertCircle size={14} color="var(--bad)" strokeWidth={2} style={{ flexShrink: 0, marginTop: '1px' }} aria-hidden="true" />
                <p style={{ margin: 0, fontSize: '13px', color: 'var(--bad)', fontFamily: 'var(--font-body)', lineHeight: 1.4 }}>
                  {requestError}
                </p>
              </div>
            )}

            {/* Confirm button */}
            <button
              type="button"
              className="btnx primary"
              disabled={!selectedSlot || requestMutation.isPending}
              onClick={() => {
                if (!selectedSlot) return;
                requestMutation.mutate({ start_utc: selectedSlot.start_utc, note });
              }}
              aria-disabled={!selectedSlot || requestMutation.isPending}
              style={{ width: '100%', height: '48px' }}
            >
              <CalendarDays size={16} aria-hidden="true" />
              {requestMutation.isPending ? 'Requesting…' : selectedSlot
                ? `Confirm — ${formatSlotTime(selectedSlot.start_utc)}`
                : 'Select a time above'}
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
